import C from "@/modules/error/ErrorFallback.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { resetConfigOverridesAtom, resetConfigOverridesTitle } from "@/config.override.ts";
import { ApiCredentialsError } from "@/modules/api/ApiError.ts";
import { getResourceCreateLink } from "@/modules/api/resourceCreateLinks.ts";
import { ResourceNotFoundError } from "@/modules/api/resourceTypeException.tsx";
import { ErrorBox } from "@/modules/error/ErrorBox.tsx";
import { notifyError } from "@/modules/error/errorReporting.ts";
import { ReportingErrorBoundary, type ReportingErrorBoundaryProps } from "@/modules/error/ReportingErrorBoundary.tsx";
import { isZodError, ZodErrorMessage } from "@/modules/error/ZodError.tsx";
import { errorToReactMessage } from "@/modules/notifications/errorToReactMessage.tsx";
import { LogoutButton } from "@/modules/user/LogoutButton.tsx";
import { mcn, type BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { Link, type NotFoundRouteProps } from "@tanstack/react-router";
import { useSetAtom } from "jotai/react";
import { Suspense, useEffect } from "react";

/** Render an error object into an appropriate, type-specific error message */
export function ErrorMessage({ error }: { error: unknown }) {
    if (error instanceof ResourceNotFoundError) {
        const { resourceTypeName } = error;
        return (
            <>
                {/* NB: Padding rather than a container gap, since this renders into containers that are
                    not stacks (see `ErrorRow`) next to content that brings its own margins. */}
                <h1 className="text-muted font-size-heading pb-4">
                    <Trans comment="This resource was not found">This {resourceTypeName} is gone</Trans>
                </h1>
                <p className="pb-5 text-muted">
                    <Trans>Time to move on. This item is deleted or you do not have access.</Trans>
                </p>
            </>
        );
    } else if (import.meta.env.DEV && isZodError(error)) {
        return (
            <>
                <h1 className="font-size-heading pb-4">Validation Error</h1>
                <ZodErrorMessage error={error} />
            </>
        );
    }
    return <div className="text-left my-1">{errorToReactMessage(error)}</div>;
}

const DashboardLink = () => (
    <Link to="/" className="text-primary">
        <b>
            <Trans>Go to dashboard</Trans>
        </b>
    </Link>
);

export function NotFoundPage(_props: NotFoundRouteProps) {
    return (
        <div className={cn("size-full text-center flex items-center justify-center bg-layout-2", C.ErrorHolder)}>
            <div className="p-4 VStack gap-4 items-center">
                <MaskIcon className="jp-cactus-icon size-5rem color-grey-4" />
                <h1 className="text-muted font-size-heading">
                    <Trans comment="This page was not found">This page is gone</Trans>
                </h1>
                <p className="text-muted">
                    <Trans comment="This page was not found">
                        Time to move on. Here are some ideas where to go next:
                    </Trans>
                </p>
                <div className="flex justify-center gap-4">
                    <DashboardLink />
                </div>
            </div>
        </div>
    );
}

interface ErrorFallbackProps {
    error: unknown;
}

export function ErrorFallback({ error }: ErrorFallbackProps) {
    let customAction;
    if (error instanceof ResourceNotFoundError) {
        const { type, resourceTypeName } = error;
        const linkProps = getResourceCreateLink(type);
        if (linkProps) {
            customAction = (
                <Link {...linkProps} className="text-primary">
                    <b>
                        <Trans>Create a new {resourceTypeName}</Trans>
                    </b>
                </Link>
            );
        }
    } else if (error instanceof ApiCredentialsError) {
        customAction = <LogoutButton />;
    }

    return (
        <ErrorBox>
            <ErrorMessage error={error} />
            <div className="flex flex-wrap flex-col justify-center gap-2">
                {customAction}
                <DashboardLink />
                {import.meta.env.DEV && (
                    <>
                        <ResetConfigOverridesButton />
                        <LogoutButton />
                    </>
                )}
            </div>
        </ErrorBox>
    );
}

export function ErrorRow({ error, tags, ...props }: { error: unknown; tags: string | string[] } & BaseProps) {
    useEffect(() => {
        if (error == null) {
            return;
        }

        notifyError(error, {
            tags: ["ErrorRow", ...(typeof tags === "string" ? [tags] : tags)],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    return (
        <div {...mcn("bg-error-light p-2 b b-b-solid b-gray-2", props)}>
            <div>Error:</div>
            <ErrorMessage error={error} />
        </div>
    );
}

export interface WErrorBoundaryProps extends Omit<ReportingErrorBoundaryProps, "FallbackComponent"> {}

export function WErrorBoundary(props: WErrorBoundaryProps) {
    return <ReportingErrorBoundary FallbackComponent={ErrorFallback} {...props} />;
}

export interface SuspenseErrorBoundaryProps extends WErrorBoundaryProps {
    /** Minimum height of the loading placeholder (to prevent layout shift) */
    loadingHeight?: string;
}

/** Error-boundary with built-in {@link Suspense} */
export function SuspenseErrorBoundary({ loadingHeight, children, ...props }: SuspenseErrorBoundaryProps) {
    return (
        <WErrorBoundary {...props}>
            <Suspense
                fallback={
                    <div
                        className="flex justify-center items-center"
                        style={loadingHeight ? { minHeight: loadingHeight } : undefined}
                    >
                        <Loading icon="none" />
                    </div>
                }
            >
                {children}
            </Suspense>
        </WErrorBoundary>
    );
}

/** In case a debug override causes the entire page to error out, offer a backup option */
function ResetConfigOverridesButton() {
    const resetConfig = useSetAtom(resetConfigOverridesAtom);
    return <WButton icon="i-lucide:bug" action={resetConfig} label={resetConfigOverridesTitle} />;
}
